import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Textarea } from '../ui/textarea';
import cn from '../../lib/utils';

/**
 * Interface representing the props for the FormTextarea component.
 *
 * - title - The label to display
 * - mandatory - Indicates whether the field is required, it's optional.
 * - hint - An optional helper text providing additional information.
 * - id - The unique identifier for the input field.
 * - val - The current value of the input field.
 * - setState - Callback function to update the state with the input field's value.
 * - err - An error message displayed if there's an issue with the input.
 */
interface FormTextareaProps {
  title: string;
  mandatory?: boolean;
  hint?: string;
  id: string;
  val: string;
  setState: (value: string) => void;
  err?: string;
}

/**
 * FormTextarea component renders a customizable textarea with label, hint, and error handling.
 * It wraps the Shadcn UI Textarea component with form-specific functionality.
 *
 * @param title - The label of the textarea.
 * @param mandatory - Indicates whether the textarea is mandatory. Default is true.
 * @param hint - Optional text providing additional instructions.
 * @param id - The unique identifier of the textarea element.
 * @param val - The current value of the textarea.
 * @param setState - The function to update the state of the textarea value.
 * @param err - Optional error message displayed when there's an issue with input.
 */
const FormTextarea = ({
  title,
  mandatory = true,
  hint,
  id,
  val,
  setState,
  err,
}: FormTextareaProps) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className='space-y-2'>
      <div className='text-sm font-medium'>
        {title}
        {mandatory ? <span className='text-red-500 ml-1'>*</span> : ''}
      </div>
      {hint && <div className='text-xs text-gray-500 italic ml-2'>{hint}</div>}
      <Textarea
        id={id}
        className={cn('w-full', err && 'border-red-500 focus-visible:ring-red-500')}
        value={val}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setState(e.target.value)}
      />
      {err && <div className='text-sm text-red-500'>{err}</div>}

      {val && (
        <div className='mt-4'>
          <button
            type='button'
            className='text-xs text-blue-600 hover:underline mb-2'
            onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? 'Hide Markdown Preview' : 'Show Markdown Preview'}
          </button>

          {showPreview && (
            <div className='p-3 border border-gray-200 rounded bg-gray-50'>
              <div className='prose prose-sm max-w-none'>
                <ReactMarkdown>{val}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FormTextarea;
